import { useFunnelState, PHASE_COUNT } from './useFunnelState'
import type { FunnelHook } from './useFunnelState'
import Questions from './Questions'
import { MedilaneLockup } from '../../components/brand/Logo'
import './funnel.css'

const PHASE_LABELS: Record<number, { label: string; icon: string }> = {
  1: { label: 'Grunddaten', icon: '👤' },
  2: { label: 'Beruf & Qualifikation', icon: '📋' },
  3: { label: 'Arbeitszeit & Rahmen', icon: '🎯' },
  4: { label: 'Wechselgründe', icon: '💭' },
  5: { label: 'Was ist Ihnen wichtig?', icon: '⭐' },
  6: { label: 'No-Gos & Begleitung', icon: '🛡️' },
  7: { label: 'Abschluss', icon: '✅' },
}

export default function FunnelLayout() {
  const funnel = useFunnelState()

  if (funnel.submitted) {
    return <SuccessPage funnel={funnel} />
  }

  const { currentQuestion, currentPhase, phaseSubIndex, phaseSubTotal, phaseProgress } = funnel
  const phases = Array.from({ length: PHASE_COUNT }, (_, i) => i + 1)

  return (
    <div className="funnel-page">
      <div className="funnel-container">
        <header className="funnel-header">
          <MedilaneLockup size={32} />
        </header>

        {funnel.hasRecovery && (
          <div className="recovery-banner animate-fade-in-up">
            <div className="recovery-content">
              <span>👋</span>
              <div>
                <strong>Willkommen zurück!</strong>
                <p>Sie haben bereits Daten eingegeben. Möchten Sie weitermachen?</p>
              </div>
            </div>
            <div className="recovery-actions">
              <button className="funnel-btn funnel-btn--primary funnel-btn--sm" onClick={funnel.recoverState}>
                Weitermachen
              </button>
              <button className="funnel-btn funnel-btn--ghost funnel-btn--sm" onClick={funnel.dismissRecovery}>
                Neu starten
              </button>
            </div>
          </div>
        )}

        {/* Phasen-Fortschritt */}
        <div className="funnel-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${phaseProgress}%` }} />
          </div>
          <div className="progress-steps">
            {phases.map((phase) => (
              <button
                key={phase}
                className={`progress-step ${phase === currentPhase ? 'progress-step--active' : ''} ${phase < currentPhase ? 'progress-step--done' : ''}`}
                onClick={() => phase < currentPhase && funnel.goToPhase(phase)}
                disabled={phase > currentPhase}
                title={PHASE_LABELS[phase].label}
              >
                <span className="progress-step-dot">{phase < currentPhase ? '✓' : phase}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Phasen-Kopf */}
        <div className="phase-header">
          <div className="phase-header-inner">
            <span className="phase-icon">{currentQuestion.phaseIcon}</span>
            <div className="phase-info">
              <span className="phase-label">{currentQuestion.phaseLabel}</span>
              {phaseSubTotal > 1 && (
                <div className="sub-dots">
                  {Array.from({ length: phaseSubTotal }, (_, i) => (
                    <span
                      key={i}
                      className={`sub-dot ${i === phaseSubIndex ? 'sub-dot--active' : ''} ${i < phaseSubIndex ? 'sub-dot--done' : ''}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Frage */}
        <div className="question-wrapper" key={currentQuestion.questionId}>
          <div className="question-card animate-slide-in">
            <h2 className="question-title">{currentQuestion.questionLabel}</h2>
            <div className="question-content">
              <Questions funnel={funnel} questionId={currentQuestion.questionId} />
            </div>
          </div>
        </div>

        {/* Navigation */}
        {currentQuestion.questionId !== 'consent' && (
          <div className="funnel-nav">
            {!funnel.isFirstQuestion && (
              <button className="funnel-btn funnel-btn--secondary" onClick={funnel.prev}>
                ← Zurück
              </button>
            )}
            <button className="funnel-btn funnel-btn--primary" onClick={funnel.next}>
              Weiter →
            </button>
          </div>
        )}

        <footer className="funnel-footer">
          <span>🔒 Ihre Daten werden vertraulich behandelt</span>
          <span>DSGVO-konform · SSL-verschlüsselt</span>
        </footer>
      </div>
    </div>
  )
}

function SuccessPage({ funnel }: { funnel: FunnelHook }) {
  return (
    <div className="funnel-page">
      <div className="funnel-container">
        <div className="funnel-success animate-scale-in">
          <div className="success-icon">🎉</div>
          <h1>Vielen Dank!</h1>
          <p>Ihr Wechselprofil wurde erfolgreich übermittelt.</p>
          <p className="success-detail">
            Wir gleichen Ihr Profil mit passenden Arbeitgebern ab und melden uns innerhalb von{' '}
            <strong>48 Stunden</strong> bei Ihnen.
          </p>
          <div className="success-summary">
            <span>
              📧 Bestätigung an: <strong>{funnel.data.email}</strong>
            </span>
          </div>
          <button className="funnel-btn funnel-btn--secondary" onClick={funnel.reset}>
            Neues Profil erstellen
          </button>
        </div>
      </div>
    </div>
  )
}
